//utils/utils.js
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { signOut } from "next-auth/react";

/**
 * Formatea timestamp para la lista de conversaciones
 * Muestra: Hoy (HH:mm), Ayer, día de la semana, o DD/MM/YY
 */
export const timestampToDate = (timestamp) => {
  if (!timestamp || isNaN(new Date(timestamp).getTime())) {
    return ""; // Valor predeterminado en caso de error
  }

  const date = new Date(timestamp);
  const today = new Date();
  
  // Resetear horas para comparaciones de días
  const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  // Calcular diferencia en días
  const diffTime = todayWithoutTime - dateWithoutTime;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Si es hoy: mostrar solo hora en formato 24h
  if (diffDays === 0) {
    return new Intl.DateTimeFormat("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  }
  
  // Si es ayer
  if (diffDays === 1) {
    return "Ayer";
  }
  
  // Si es de esta semana (últimos 7 días): mostrar nombre del día
  if (diffDays >= 2 && diffDays <= 6) {
    const dayNames = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    return dayNames[date.getDay()];
  }
  
  // Si es más de 7 días: mostrar fecha corta DD/MM/YY
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(date);
};

/**
 * Formatea timestamp solo como hora para mensajes del chat
 * Siempre muestra: HH:mm (formato 24h)
 */
export const timestampToTime = (timestamp) => {
  if (!timestamp || isNaN(new Date(timestamp).getTime())) {
    return "";
  }

  const date = new Date(timestamp);
  return new Intl.DateTimeFormat("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

export function findItemByKey(items, key) {
  for (const item of items) {
    if (item.key === key) {
      return item;
    }
    if (item.items) {
      const found = findItemByKey(item.items, key);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export async function fetchWithAuth(url, options = {}) {
  const timeout = options.timeout || 30000;
  let controller, timeoutId;
  if (!options.signal) {
    controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), timeout);
  }
  try {
    const res = await fetch(url, {
      ...options,
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (res.status === 401 && typeof window !== "undefined") {
      await signOut({ callbackUrl: "/login" });
      return;
    }
    return res;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export async function fetchWithAuthServer(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return res;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function convertirArchivoABase64(archivo) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(archivo);
  });
}

export function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export function cn(...inputs) {
  return twMerge(clsx(...inputs));
}

export function convertToSelectOptions(items, valueKey, labelKey) {
  if (!Array.isArray(items)) {
    console.error("Error: items is not an array");
    return [];
  }
  return items.map((item) => ({
    value: String(item[valueKey]),
    label: String(item[labelKey]),
  }));
}

export function cleanObject(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  );
}

/**
 * Extrae y formatea el texto del error de un mensaje
 * Maneja diferentes estructuras de error (Meta API, errores genéricos, etc.)
 */
/**
 * Verifica si el error es "Message undeliverable"
 * Esto indica que el usuario no existe o no tiene WhatsApp
 */
export function isUndeliverableError(error) {
  if (!error) return false;
  
  // Buscar en diferentes estructuras del error
  const errorText = getErrorMessage(error).toLowerCase();
  return errorText.includes("undeliverable") || 
         errorText.includes("message undeliverable") ||
         errorText.includes("131026");
}

export function getErrorMessage(error) {
  if (!error) return "Error desconocido";
  
  // Si es un booleano (true), significa que hay error pero no hay detalles
  if (typeof error === "boolean") {
    return error ? "Error al enviar el mensaje" : "Error desconocido";
  }
  
  // Si es un string, devolverlo directamente
  if (typeof error === "string") {
    return error;
  }
  
  // Si es un objeto vacío o null
  if (typeof error === "object" && (Object.keys(error).length === 0 || error === null)) {
    return "Error desconocido";
  }
  
  // Caso 1: Error de queue.js con estructura {error: true, message: {...}}
  if (error.error === true && error.message) {
    let metaError = error.message;
    
    // Si message es un string JSON, intentar parsearlo
    if (typeof metaError === "string") {
      try {
        const parsed = JSON.parse(metaError);
        if (parsed && typeof parsed === "object") {
          metaError = parsed;
        }
      } catch {
        // Si no se puede parsear, usar el string directamente
        return metaError;
      }
    }
    
    // Si message es un objeto con estructura de Meta API
    if (typeof metaError === "object" && metaError !== null && metaError.message) {
      let errorText = metaError.message || "";
      
      // Agregar detalles si existen
      if (metaError.error_data?.details) {
        errorText += `\n${metaError.error_data.details}`;
      }
      
      // Agregar código de error si existe
      if (metaError.code) {
        errorText = `[${metaError.code}] ${errorText}`;
      }
      
      return errorText;
    }
    // Si message es un string (después de intentar parsear)
    if (typeof metaError === "string") {
      return metaError;
    }
  }
  
  // Caso 2: Error directo de Meta API (estructura anidada) o Error de JavaScript desde MongoDB
  if (error.message) {
    let messageValue = error.message;
    
    // Si message es un string JSON, intentar parsearlo
    if (typeof messageValue === "string" && messageValue.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(messageValue);
        if (parsed && typeof parsed === "object") {
          // Si el objeto parseado tiene error: true y message, usar ese
          if (parsed.error === true && parsed.message) {
            return getErrorMessage(parsed); // Recursivamente procesar el objeto parseado
          }
          // Si tiene message dentro, usar ese
          if (parsed.message) {
            messageValue = parsed.message;
          } else {
            messageValue = parsed;
          }
        }
      } catch {
        // Si no se puede parsear, usar el string directamente
      }
    }
    
    // Si messageValue es un objeto (estructura anidada)
    if (typeof messageValue === "object" && messageValue !== null && messageValue.message) {
      const metaError = messageValue;
      let errorText = metaError.message || "";
      
      // Agregar detalles si existen
      if (metaError.error_data?.details) {
        errorText += `\n${metaError.error_data.details}`;
      }
      
      // Agregar código de error si existe
      if (metaError.code) {
        errorText = `[${metaError.code}] ${errorText}`;
      }
      
      return errorText;
    }
    
    // Si messageValue es un string directo
    if (typeof messageValue === "string") {
      return messageValue;
    }
  }
  
  // Caso 3: Error del webhook de Meta (status failed) - estructura plana
  // {code: 131026, title: "...", message: "...", error_data: {details: "..."}}
  if (error.code && error.message) {
    let errorText = error.message || error.title || "";
    
    // Agregar detalles si existen
    if (error.error_data?.details) {
      errorText += `\n${error.error_data.details}`;
    }
    
    // Agregar código de error
    errorText = `[${error.code}] ${errorText}`;
    
    return errorText;
  }
  
  // Caso 4: Si tiene error_data con details (sin message)
  if (error.error_data?.details) {
    return error.error_data.details;
  }
  
  // Caso 5: Si tiene title pero no message
  if (error.title) {
    return error.title;
  }
  
  // Caso 6: Si tiene una propiedad 'error' con mensaje
  if (error.error && typeof error.error === "string") {
    return error.error;
  }
  
  // Fallback: intentar convertir a string
  try {
    // Si es un objeto complejo, intentar extraer información útil
    if (typeof error === "object" && error !== null) {
      // Si es un objeto vacío, retornar mensaje por defecto
      const keys = Object.keys(error);
      if (keys.length === 0) {
        return "Error al enviar el mensaje";
      }
      
      // Buscar cualquier propiedad que contenga "message" o "error"
      for (const key in error) {
        if (key.toLowerCase().includes("message") || key.toLowerCase().includes("error")) {
          const value = error[key];
          if (typeof value === "string" && value.trim()) {
            return value;
          }
          // Si el valor es un objeto, intentar extraer su mensaje
          if (typeof value === "object" && value !== null && value.message) {
            return value.message;
          }
        }
      }
      
      // Si tiene alguna propiedad con valor string, usarla
      for (const key in error) {
        const value = error[key];
        if (typeof value === "string" && value.trim() && value !== "true" && value !== "false") {
          return value;
        }
      }
      
      // Último recurso: convertir a JSON (pero limitar longitud)
      const jsonStr = JSON.stringify(error);
      return jsonStr.length > 200 ? jsonStr.substring(0, 200) + "..." : jsonStr;
    }
    return String(error);
  } catch {
    return "Error al enviar el mensaje";
  }
}

export function hasInformacionContacto(info) {
  return Boolean(
    info &&
      typeof info === "object" &&
      !Array.isArray(info) &&
      Object.keys(info).length > 0
  );
}

export function mergeInformacionContacto(...sources) {
  for (const source of sources) {
    if (hasInformacionContacto(source)) {
      return { ...source };
    }
  }

  for (const source of sources) {
    if (source && typeof source === "object" && !Array.isArray(source)) {
      return { ...source };
    }
  }

  return {};
}

export function hasCrmData(crm) {
  if (!crm || typeof crm !== "object" || Array.isArray(crm)) return false;

  return Boolean(
    crm.etapa ||
      crm.estado ||
      crm.tipo ||
      crm.fuente ||
      crm.scoring ||
      crm.valorEstimado ||
      crm.actualizadoPor ||
      crm.actualizadoEn ||
      (Array.isArray(crm.notas) && crm.notas.length > 0) ||
      (Array.isArray(crm.tareas) && crm.tareas.length > 0)
  );
}

export function mergeCrm(...sources) {
  for (const source of sources) {
    if (hasCrmData(source)) {
      return { ...source };
    }
  }

  for (const source of sources) {
    if (source && typeof source === "object" && !Array.isArray(source)) {
      return { ...source };
    }
  }

  return {};
}
